package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.web.components.*
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.core.locale.LocalStrings
import app.footyguide.web.theme.captureThemeSnapshot
import app.footyguide.web.theme.restore
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.Team
import app.footyguide.domain.model.MatchStatus
import app.footyguide.domain.model.isLive
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.data.WebTeamRepository
import app.footyguide.web.data.HighlightlyLiveRefreshApi
import app.footyguide.web.data.WebChannelRepository
import app.footyguide.web.data.WebCompetitionRepository
import app.footyguide.web.todayFormatted
import app.footyguide.web.route.WebRoutes
import app.footyguide.web.route.navigateTo
import kotlinx.browser.window
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.plus

fun resolveBroadcasterType(channelId: String, strings: app.footyguide.core.locale.AppStrings? = null): String {
    val clean = channelId.lowercase()
    return when {
        clean.contains("globo") || clean.contains("sbt") || clean.contains("record") || clean.contains("band") -> "TV Aberta"
        clean.contains("sportv") || clean.contains("espn") || clean.contains("bandsports") || clean.contains("tnt") -> "TV Fechada"
        clean.contains("premiere") || clean.contains("combate") || clean.contains("nosso futebol") -> "Pay-per-view"
        clean.contains("cazetv") || clean.contains("prime") || clean.contains("paramount") || clean.contains("disney") || clean.contains("max") || clean.contains("youtube") || clean.contains("globoplay") -> "Streaming"
        else -> strings?.officialBroadcast ?: "Transmissão Oficial"
    }
}

fun buildGameShareText(
    homeName: String,
    awayName: String,
    kickoffTime: String,
    dateLabel: String,
    channels: List<String>,
    strings: app.footyguide.core.locale.AppStrings? = null
): String {
    val formattedChannels = if (channels.isNotEmpty()) {
        channels.joinToString(", ") { formatChannelName(it) }
    } else {
        strings?.noChannels ?: "A confirmar"
    }
    return "⚽ $homeName × $awayName\n" +
        "📅 $dateLabel · $kickoffTime\n" +
        "📺 $formattedChannels\n" +
        "👉 FootyGuide"
}

@Deprecated("Use buildGameShareText for generic cross-platform sharing")
fun buildWhatsAppShareText(
    homeName: String,
    awayName: String,
    kickoffTime: String,
    dateLabel: String,
    channels: List<String>
): String = buildGameShareText(homeName, awayName, kickoffTime, dateLabel, channels)

fun buildAndroidShareIntentUrl(shareText: String): String {
    val encoded = js("encodeURIComponent(shareText)") as String
    return "intent:#Intent;action=android.intent.action.SEND;type=text/plain;S.android.intent.extra.TEXT=$encoded;end"
}

fun isAndroidDevice(): Boolean {
    return js("(/Android/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || ''))") as Boolean
}

fun shareGame(title: String, text: String) {
    val currentUrl = try { window.location.href } catch (_: Throwable) { "https://footyguide.app" }
    val shareText = "$text\n$currentUrl"
    val canShare = js("typeof window !== 'undefined' && typeof window.navigator !== 'undefined' && typeof window.navigator.share === 'function'") as Boolean
    if (canShare) {
        val nav = window.navigator.asDynamic()
        val shareData = js("{}")
        shareData.text = shareText
        val promise = nav.share(shareData)
        promise.catch { err: dynamic ->
            val msg = (err?.message as? String ?: err?.toString() as? String ?: "").lowercase()
            val canceled = msg.contains("abort") || msg.contains("cancel")
            if (!canceled && isAndroidDevice()) {
                window.location.href = buildAndroidShareIntentUrl(shareText)
            }
        }
    } else if (isAndroidDevice()) {
        window.location.href = buildAndroidShareIntentUrl(shareText)
    } else {
        val encoded = js("encodeURIComponent(shareText)") as String
        window.open("https://api.whatsapp.com/send?text=$encoded", "_blank")
    }
}

data class CalendarTimes(
    val startIso: String,
    val endIso: String,
    val dtstamp: String,
    val beginEpochMs: Long = 0L,
    val endEpochMs: Long = 0L,
)

fun calculateCalendarTimes(date: String, kickoffTime: String): CalendarTimes {
    val cleanDate = date.replace("-", "")
    val timeParts = kickoffTime.split(":")
    val hourInt = timeParts.getOrNull(0)?.toIntOrNull() ?: 16
    val minInt = timeParts.getOrNull(1)?.toIntOrNull() ?: 0
    val hourStr = hourInt.toString().padStart(2, '0')
    val minStr = minInt.toString().padStart(2, '0')
    val startIso = "${cleanDate}T${hourStr}${minStr}00"

    val rawEndHour = hourInt + 2
    val endIso = if (rawEndHour >= 24) {
        val nextDateStr = try {
            kotlinx.datetime.LocalDate.parse(date).plus(kotlinx.datetime.DatePeriod(days = 1)).toString().replace("-", "")
        } catch (_: Exception) {
            cleanDate
        }
        val endHourStr = (rawEndHour - 24).toString().padStart(2, '0')
        "${nextDateStr}T${endHourStr}${minStr}00"
    } else {
        val endHourStr = rawEndHour.toString().padStart(2, '0')
        "${cleanDate}T${endHourStr}${minStr}00"
    }

    val isoWithOffset = "${date}T${hourStr}:${minStr}:00-03:00"
    val parsedMs = (js("Date.parse(isoWithOffset)") as? Double)?.toLong() ?: 0L
    val beginMs = if (parsedMs > 0L) parsedMs else 0L
    val endMs = if (beginMs > 0L) beginMs + 2L * 60L * 60L * 1000L else 0L

    val dtstamp = js("new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'") as String
    return CalendarTimes(startIso, endIso, dtstamp, beginMs, endMs)
}

fun buildGoogleCalendarUrl(
    homeName: String,
    awayName: String,
    date: String,
    kickoffTime: String,
    channels: List<String>
): String {
    val times = calculateCalendarTimes(date, kickoffTime)
    val title = "$homeName × $awayName"
    val channelText = if (channels.isNotEmpty()) channels.joinToString(", ") { formatChannelName(it) } else "Transmissão Oficial"
    val details = "⚽ Partida: $homeName × $awayName\n📺 Onde assistir: $channelText\n👉 FootyGuide"

    val encodedTitle = js("encodeURIComponent(title)") as String
    val encodedDetails = js("encodeURIComponent(details)") as String
    return "https://calendar.google.com/calendar/render?action=TEMPLATE&text=$encodedTitle&dates=${times.startIso}/${times.endIso}&ctz=America/Sao_Paulo&details=$encodedDetails"
}

fun buildAndroidCalendarIntentUrl(
    homeName: String,
    awayName: String,
    date: String,
    kickoffTime: String,
    channels: List<String>
): String {
    val times = calculateCalendarTimes(date, kickoffTime)
    val title = "$homeName × $awayName"
    val channelText = if (channels.isNotEmpty()) channels.joinToString(", ") { formatChannelName(it) } else "Transmissão Oficial"
    val details = "⚽ Partida: $homeName × $awayName\n📺 Onde assistir: $channelText\n👉 FootyGuide"
    val fallbackUrl = buildGoogleCalendarUrl(homeName, awayName, date, kickoffTime, channels)

    val encTitle = js("encodeURIComponent(title)") as String
    val encDetails = js("encodeURIComponent(details)") as String
    val encFallback = js("encodeURIComponent(fallbackUrl)") as String
    return "intent://com.android.calendar/events#Intent;scheme=content;action=android.intent.action.INSERT;type=vnd.android.cursor.dir/event;S.title=$encTitle;S.description=$encDetails;l.beginTime=${times.beginEpochMs};l.endTime=${times.endEpochMs};S.browser_fallback_url=$encFallback;end"
}

fun buildIcsContent(
    homeName: String,
    awayName: String,
    date: String,
    kickoffTime: String,
    channels: List<String>
): String {
    val times = calculateCalendarTimes(date, kickoffTime)
    val cleanDate = date.replace("-", "")
    val channelText = if (channels.isNotEmpty()) channels.joinToString(", ") { formatChannelName(it) } else "Transmissão Oficial"

    return """
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//FootyGuide//App//PT
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${cleanDate}_${homeName.replace(" ", "")}_vs_${awayName.replace(" ", "")}@footyguide.app
DTSTAMP:${times.dtstamp}
SUMMARY:$homeName × $awayName
DESCRIPTION:⚽ Partida: $homeName × $awayName\n📺 Onde assistir: $channelText\n👉 FootyGuide (https://footyguide.app)
DTSTART;TZID=America/Sao_Paulo:${times.startIso}
DTEND;TZID=America/Sao_Paulo:${times.endIso}
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:O jogo $homeName × $awayName começa em 15 minutos!
TRIGGER:-PT15M
END:VALARM
END:VEVENT
END:VCALENDAR
    """.trimIndent().trim()
}

fun downloadIcsFile(fileName: String, content: String) {
    js("""
        var blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function() { URL.revokeObjectURL(url); }, 2000);
    """)
}

fun isIosMobileDevice(): Boolean {
    return js("""
        (function() {
            var ua = navigator.userAgent || '';
            var isIOS = /iPhone|iPod/.test(ua) || /iPad/.test(ua);
            var isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
            return isIOS || isIpadOs;
        })()
    """) as Boolean
}

fun openIosCalendarEventSheet(content: String) {
    val encoded = js("encodeURIComponent(content)") as String
    window.location.href = "data:text/calendar;charset=utf-8,$encoded"
}

fun addToSystemCalendar(
    homeName: String,
    awayName: String,
    date: String,
    kickoffTime: String,
    channels: List<String>
) {
    when {
        isAndroidDevice() -> {
            window.location.href = buildAndroidCalendarIntentUrl(homeName, awayName, date, kickoffTime, channels)
        }
        isIosMobileDevice() -> {
            val ics = buildIcsContent(homeName, awayName, date, kickoffTime, channels)
            openIosCalendarEventSheet(ics)
        }
        else -> {
            val googleUrl = buildGoogleCalendarUrl(homeName, awayName, date, kickoffTime, channels)
            window.open(googleUrl, "_blank")
        }
    }
}

/**
 * Stitch Design System (Screen 4: Detalhe do Jogo - Onde Assistir e Escalação).
 * Broad-Sheet Paper & Ink: #FAFAF7 substrate, #FFFFFF cards, #E7E7E1 borders,
 * monumental matchup, 2-column visual broadcaster grid and WhatsApp share action.
 */
fun shouldShowRemindButton(matchStatus: MatchStatus, isLive: Boolean): Boolean {
    if (isLive) return false
    return matchStatus == MatchStatus.NOT_STARTED
}

@Composable
fun GameDetailPage(
    gameId: String,
    gameRepository: WebGameRepository,
    teamRepository: WebTeamRepository,
    channelRepository: WebChannelRepository,
    competitionRepository: WebCompetitionRepository,
    onBack: () -> Unit,
    onNavigate: (String) -> Unit = { navigateTo(it) }
) {
    val strings = LocalStrings.current
    var game by remember(gameId) { mutableStateOf<Game?>(null) }
    var teams by remember { mutableStateOf<Map<String, Team>>(emptyMap()) }
    var channels by remember { mutableStateOf<Map<String, WebChannelRepository.ChannelData>>(emptyMap()) }
    var competitions by remember { mutableStateOf<Map<String, WebCompetitionRepository.CompetitionData>>(emptyMap()) }
    var isLoading by remember(gameId) { mutableStateOf(true) }
    val themeSnapshot = remember(gameId) { captureThemeSnapshot() }

    LaunchedEffect(gameId) {
        isLoading = true
        try {
            val loaded = gameRepository.getGameById(gameId)
            game = loaded

            val rawTeams = teamRepository.getAll()
            val mappedTeams = rawTeams.mapValues { (_, data) ->
                Team(
                    id = data.id,
                    name = data.name,
                    shortName = data.shortName,
                    country = "BR",
                    league = "brasileirao",
                    logo = data.logo,
                    primaryColor = data.primaryColor,
                    secondaryColor = data.secondaryColor,
                    tertiaryColor = data.tertiaryColor,
                    isDark = data.isDark
                )
            }.toMutableMap()

            val homeId = loaded?.homeTeamId
            if (homeId != null && !mappedTeams.containsKey(homeId)) {
                val club = ClubCatalog.bySlug(homeId)
                mappedTeams[homeId] = Team(
                    id = homeId,
                    name = club?.name ?: homeId.replaceFirstChar { it.uppercase() },
                    shortName = club?.initials ?: homeId.take(3).uppercase(),
                    country = "BR",
                    league = "brasileirao",
                    logo = club?.logoUrl ?: "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/teams/$homeId.webp",
                    primaryColor = club?.c1 ?: "#16181D",
                    secondaryColor = club?.c2 ?: "#FFFFFF",
                    tertiaryColor = club?.c3,
                    isDark = true
                )
            }
            val awayId = loaded?.awayTeamId
            if (awayId != null && !mappedTeams.containsKey(awayId)) {
                val club = ClubCatalog.bySlug(awayId)
                mappedTeams[awayId] = Team(
                    id = awayId,
                    name = club?.name ?: awayId.replaceFirstChar { it.uppercase() },
                    shortName = club?.initials ?: awayId.take(3).uppercase(),
                    country = "BR",
                    league = "brasileirao",
                    logo = club?.logoUrl ?: "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/teams/$awayId.webp",
                    primaryColor = club?.c1 ?: "#16181D",
                    secondaryColor = club?.c2 ?: "#FFFFFF",
                    tertiaryColor = club?.c3,
                    isDark = true
                )
            }

            teams = mappedTeams
            channels = channelRepository.getAll()
            competitions = competitionRepository.getAll()
        } catch (_: Throwable) {
        } finally {
            isLoading = false
        }
    }

    val liveRefreshEnabled = shouldEnableDetailLiveRefresh(game, todayFormatted())

    LiveRefreshEffect(
        enabled = liveRefreshEnabled,
        leaseKey = game?.let { buildLiveRefreshKey(it.date, it.competitionId) },
        watchPath = "games/${game?.date ?: todayFormatted()}/matches",
        intervalMs = DEFAULT_LIVE_REFRESH_INTERVAL_MS,
        onRefresh = {
            val current = game ?: return@LiveRefreshEffect
            if (!HighlightlyLiveRefreshApi.refresh(
                date = current.date,
                competitionId = current.competitionId,
                ttlMinutes = DEFAULT_LIVE_REFRESH_TTL_MINUTES,
            )) {
                error("live refresh failed")
            }
            game = gameRepository.getGameById(gameId)
        },
        onBroadcastRefresh = { liveScores ->
            val current = game ?: return@LiveRefreshEffect
            if (liveScores.isEmpty()) {
                gameRepository.refreshGameById(gameId, current.date)?.let { game = it }
            } else {
                game = gameRepository.applyLiveScores(listOf(current), liveScores).single()
            }
        },
    )

    DisposableEffect(gameId) {
        onDispose {
            themeSnapshot.restore()
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("#FAFAF7"))
        }
    }) {
        val comp = game?.competitionId?.let { competitions[it] }
        val compTitle = competitionDisplayName(game?.competitionId ?: "", comp?.shortName, comp?.name)
        AppToolbar(
            onBack = onBack,
            icon = {
                SafeImg(
                    src = comp?.logo,
                    fallbackIcon = Icons.SHIELD,
                    fallbackSize = 16,
                    modifier = {
                        width(20.px)
                        height(20.px)
                        property("object-fit", "contain")
                    }
                )
            },
            title = compTitle,
            actions = {
                val currentGame = game
                if (currentGame != null) {
                    Button({
                        onClick {
                            val hClub = ClubCatalog.bySlug(currentGame.homeTeamId)
                            val aClub = ClubCatalog.bySlug(currentGame.awayTeamId)
                            val hName = hClub?.name ?: teams[currentGame.homeTeamId]?.name ?: displayName(currentGame.homeTeamId)
                            val aName = aClub?.name ?: teams[currentGame.awayTeamId]?.name ?: displayName(currentGame.awayTeamId)
                            val kTime = extractKickoffTime(currentGame.kickoff)
                            val dLabel = formatBannerDate(currentGame.date, strings)
                            val shareText = buildGameShareText(hName, aName, kTime, dLabel, currentGame.channelsFor("BR"), strings)
                            shareGame("$hName × $aName", shareText)
                        }
                        title(strings.share)
                        style {
                            background("transparent")
                            border(0.px)
                            padding(8.px)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            borderRadius(8.px)
                        }
                    }) {
                        LucideIcon(Icons.SHARE, size = 20, color = "#16181D")
                    }
                }
            }
        )

        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color("#FAFAF7"))
                paddingBottom(60.px)
            }
        }) {


            if (isLoading) {
                GameDetailSkeleton()
            } else if (game == null) {
                Div({
                    style {
                        padding(60.px, 20.px)
                        property("text-align", "center")
                        fontFamily("Inter, sans-serif")
                        color(Color("#71717A"))
                    }
                }) { Text(strings.matchNotFound) }
            } else {
                val home = teams[game!!.homeTeamId]
                val away = teams[game!!.awayTeamId]
                val homeClub = ClubCatalog.bySlug(game!!.homeTeamId)
                val awayClub = ClubCatalog.bySlug(game!!.awayTeamId)
                val homeDisplayName = homeClub?.name ?: home?.name ?: displayName(game!!.homeTeamId)
                val awayDisplayName = awayClub?.name ?: away?.name ?: displayName(game!!.awayTeamId)
                val homeFullName = homeClub?.fullName ?: homeDisplayName
                val awayFullName = awayClub?.fullName ?: awayDisplayName
                val homeInitials = homeClub?.initials ?: resolveClubInitials(game!!.homeTeamId)
                val awayInitials = awayClub?.initials ?: resolveClubInitials(game!!.awayTeamId)
                val isNotStarted = game!!.matchStatus == MatchStatus.NOT_STARTED
                val isFinished = game!!.matchStatus == MatchStatus.FINISHED
                val isLive = game!!.isLive
                val kickoffTime = extractKickoffTime(game!!.kickoff)
                val dateLabel = formatBannerDate(game!!.date, strings)

                // MONUMENTAL MATCHUP CARD
                Div({
                    style {
                        margin(16.px, 16.px, 20.px)
                        backgroundColor(Color("#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        borderRadius(20.px)
                        padding(24.px, 16.px)
                        property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    // STATUS BADGE
                    Div({
                        style {
                            marginBottom(16.px)
                            fontSize(11.px)
                            fontWeight(800)
                            fontFamily("Inter, sans-serif")
                            property("text-transform", "uppercase")
                            letterSpacing(0.8.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            color(Color(if (isLive) "#D92D20" else "#71717A"))
                            backgroundColor(Color(if (isLive) "#FEF3F2" else "#F4F4F5"))
                            padding(4.px, 12.px)
                            borderRadius(999.px)
                        }
                    }) {
                        if (isLive) {
                            Span({
                                style {
                                    width(6.px)
                                    height(6.px)
                                    borderRadius(3.px)
                                    backgroundColor(Color("#D92D20"))
                                }
                            })
                            Text(strings.liveLabel)
                        } else if (isFinished) {
                            Text(strings.finishedLabel)
                        } else {
                            Text("$dateLabel · $kickoffTime")
                        }
                    }

                    // MATCHUP ROW: HOME - SCORE - AWAY
                    Div({
                        style {
                            width(100.percent)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                        }
                    }) {
                        // HOME TEAM
                        Div({
                            onClick {
                                val homeSlug = game?.homeTeamId ?: "flamengo"
                                onNavigate(WebRoutes.club(homeSlug))
                            }
                            style {
                                flex(1)
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                cursor("pointer")
                            }
                        }) {
                            SafeImg(
                                src = home?.logo,
                                fallbackIcon = Icons.SHIELD,
                                fallbackSize = CrestSizes.DETAIL_HEADER_FALLBACK,
                                modifier = {
                                    width(CrestSizes.DETAIL_HEADER_PX.px)
                                    height(CrestSizes.DETAIL_HEADER_PX.px)
                                    property("width", CrestSizes.DETAIL_HEADER_CSS)
                                    property("height", CrestSizes.DETAIL_HEADER_CSS)
                                    property("object-fit", "contain")
                                }
                            )
                            Span({
                                style {
                                    fontFamily("Inter, sans-serif")
                                    fontWeight(800)
                                    fontSize(17.px)
                                    color(Color("#16181D"))
                                    letterSpacing((-0.2).px)
                                    property("text-align", "center")
                                }
                            }) {
                                Text(homeDisplayName)
                            }
                            Span({
                                style {
                                    fontFamily("Inter, sans-serif")
                                    fontSize(11.px)
                                    fontWeight(500)
                                    color(Color("#71717A"))
                                    property("text-align", "center")
                                    property("line-height", "1.25")
                                }
                            }) {
                                Text(homeFullName)
                            }
                        }

                        // SCORE / VS
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                padding(0.px, 12.px)
                            }
                        }) {
                            if (isNotStarted) {
                                Span({
                                    style {
                                        fontFamily("Sora, sans-serif")
                                        fontSize(24.px)
                                        fontWeight(900)
                                        color(Color("#D4D4D8"))
                                        letterSpacing(2.px)
                                    }
                                }) {
                                    Text("VS")
                                }
                            } else {
                                Span({
                                    style {
                                        fontFamily("Sora, sans-serif")
                                        fontSize(36.px)
                                        fontWeight(900)
                                        color(Color("#16181D"))
                                        letterSpacing((-1).px)
                                    }
                                }) {
                                    Text("${game!!.homeScore ?: 0} - ${game!!.awayScore ?: 0}")
                                }
                            }
                        }

                        // AWAY TEAM
                        Div({
                            onClick {
                                val awaySlug = game?.awayTeamId ?: "palmeiras"
                                onNavigate(WebRoutes.club(awaySlug))
                            }
                            style {
                                flex(1)
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                cursor("pointer")
                            }
                        }) {
                            SafeImg(
                                src = away?.logo,
                                fallbackIcon = Icons.SHIELD,
                                fallbackSize = CrestSizes.DETAIL_HEADER_FALLBACK,
                                modifier = {
                                    width(CrestSizes.DETAIL_HEADER_PX.px)
                                    height(CrestSizes.DETAIL_HEADER_PX.px)
                                    property("width", CrestSizes.DETAIL_HEADER_CSS)
                                    property("height", CrestSizes.DETAIL_HEADER_CSS)
                                    property("object-fit", "contain")
                                }
                            )
                            Span({
                                style {
                                    fontFamily("Inter, sans-serif")
                                    fontWeight(800)
                                    fontSize(17.px)
                                    color(Color("#16181D"))
                                    letterSpacing((-0.2).px)
                                    property("text-align", "center")
                                }
                            }) {
                                Text(awayDisplayName)
                            }
                            Span({
                                style {
                                    fontFamily("Inter, sans-serif")
                                    fontSize(11.px)
                                    fontWeight(500)
                                    color(Color("#71717A"))
                                    property("text-align", "center")
                                    property("line-height", "1.25")
                                }
                            }) {
                                Text(awayFullName)
                            }
                        }
                    }
                }

                // BROADCASTER VISUAL GRID (STITCH SCREEN 4)
                Main({
                    style {
                        padding(0.px, 16.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    // SECTION HEADER
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(12.px)
                                fontWeight(800)
                                fontFamily("Inter, sans-serif")
                                color(Color("#16181D"))
                                property("text-transform", "uppercase")
                                letterSpacing(0.8.px)
                            }
                        }) {
                            Text(strings.whereToWatch.replace("📺", "").trim())
                        }
                    }

                    val gameChannels = orderChannelsForDisplay(game!!.channelsFor("BR"))
                    if (gameChannels.isEmpty()) {
                        Div({
                            style {
                                padding(24.px)
                                backgroundColor(Color("#FFFFFF"))
                                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                                borderRadius(16.px)
                                property("text-align", "center")
                            }
                        }) {
                            P({
                                style {
                                    margin(0.px)
                                    fontSize(13.px)
                                    fontFamily("Inter, sans-serif")
                                    color(Color("#71717A"))
                                }
                            }) {
                                Text(strings.noChannels)
                            }
                        }
                    } else {
                        // 2-COLUMN BROADCASTER GRID
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(2, 1fr)")
                                gap(12.px)
                            }
                        }) {
                            gameChannels.forEach { channelId ->
                                val channel = channels[channelId]
                                val channelTitle = formatChannelName(channelId)
                                val typeLabel = resolveBroadcasterType(channelId, strings)

                                Div({
                                    classes("fg-pressable", "fg-card-interactive")
                                    onClick {
                                        channel?.affiliateLink?.let { window.open(it, "_blank") }
                                    }
                                    style {
                                        backgroundColor(Color("#FFFFFF"))
                                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                                        borderRadius(14.px)
                                        padding(14.px, 12.px)
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(8.px)
                                        cursor(if (channel?.affiliateLink != null) "pointer" else "default")
                                        property("box-shadow", "0 1px 3px rgba(22, 24, 29, 0.03)")
                                    }
                                }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            justifyContent(JustifyContent.SpaceBetween)
                                        }
                                    }) {
                                        Span({
                                            style {
                                                fontSize(10.px)
                                                fontWeight(800)
                                                fontFamily("Inter, sans-serif")
                                                color(Color("#16181D"))
                                                backgroundColor(Color("#F4F4F5"))
                                                padding(3.px, 7.px)
                                                borderRadius(6.px)
                                                property("text-transform", "uppercase")
                                                letterSpacing(0.4.px)
                                            }
                                        }) {
                                            Text(channelTitle)
                                        }

                                        if (channel?.affiliateLink != null) {
                                            LucideIcon(Icons.CHEVRON_RIGHT, size = 14, color = "#0B6E4F")
                                        }
                                    }

                                    Span({
                                        style {
                                            fontSize(12.px)
                                            fontWeight(700)
                                            fontFamily("Sora, sans-serif")
                                            color(Color("#16181D"))
                                        }
                                    }) {
                                        Text(channel?.name ?: channelTitle)
                                    }

                                    Span({
                                        style {
                                            fontSize(11.px)
                                            fontFamily("Inter, sans-serif")
                                            color(Color("#71717A"))
                                            fontWeight(500)
                                        }
                                    }) {
                                        Text(typeLabel)
                                    }
                                }
                            }
                        }
                    }

                    // TACTICAL GAME SHEET (PRANCHETA EDITORIAL)
                    Div({
                        style {
                            marginTop(8.px)
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(16.px)
                            padding(16.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(10.px)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(11.px)
                                fontWeight(800)
                                fontFamily("Inter, sans-serif")
                                color(Color("#71717A"))
                                property("text-transform", "uppercase")
                                letterSpacing(0.6.px)
                            }
                        }) {
                            Text(strings.matchInfo)
                        }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                fontSize(13.px)
                                fontFamily("Inter, sans-serif")
                            }
                        }) {
                            Span({ style { color(Color("#71717A")) } }) { Text(strings.kickoffLabel) }
                            Span({ style { fontWeight(700); color(Color("#16181D")) } }) { Text(kickoffTime) }
                        }

                        val roundNumber = game!!.matchday
                            ?: game!!.stage
                                ?.takeIf { it.contains("regular", ignoreCase = true) && it.contains("season", ignoreCase = true) }
                                ?.substringAfterLast("-", "")
                                ?.trim()
                                ?.toIntOrNull()
                        val roundText = if (roundNumber != null) strings.formatRoundNumber(roundNumber) else strings.regularPhase
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                fontSize(13.px)
                                fontFamily("Inter, sans-serif")
                            }
                        }) {
                            Span({ style { color(Color("#71717A")) } }) { Text(strings.roundLabel) }
                            Span({ style { fontWeight(700); color(Color("#16181D")) } }) { Text(roundText) }
                        }
                    }

                    // ACTION BUTTONS (LEMBRAR-ME NA ESQUERDA & COMPARTILHAR NA DIREITA)
                    val shareChannels = game!!.channelsFor("BR")
                    val showRemind = shouldShowRemindButton(game!!.matchStatus, game!!.isLive)
                    Div({
                        style {
                            marginTop(12.px)
                            display(DisplayStyle.Flex)
                            gap(10.px)
                            width(100.percent)
                        }
                    }) {
                        // BOTÃO ADICIONAR NA AGENDA (SE O JOGO NÃO COMEÇOU — ESQUERDA)
                        if (showRemind) {
                            Button({
                                classes("fg-pressable", "fg-btn-bounce")
                                onClick {
                                    addToSystemCalendar(
                                        homeName = homeDisplayName,
                                        awayName = awayDisplayName,
                                        date = game!!.date,
                                        kickoffTime = kickoffTime,
                                        channels = shareChannels
                                    )
                                }
                                style {
                                    property("flex", "1 1 0%")
                                    padding(14.px, 16.px)
                                    backgroundColor(Color("#FFFFFF"))
                                    color(Color("#16181D"))
                                    borderRadius(14.px)
                                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                                    fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                                    fontSize(14.px)
                                    fontWeight(700)
                                    letterSpacing((-0.2).px)
                                    cursor("pointer")
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    gap(8.px)
                                    property("box-shadow", "0 1px 3px rgba(22, 24, 29, 0.04)")
                                }
                            }) {
                                LucideIcon(Icons.CALENDAR, size = 18, color = "#16181D")
                                Text(strings.remindMe)
                            }
                        }

                        // BOTÃO COMPARTILHAR (DIREITA OU LARGURA TOTAL SE NÃO HOUVER LEMBRAR-ME)
                        Button({
                            classes("fg-pressable", "fg-btn-bounce")
                            onClick {
                                val text = buildGameShareText(homeDisplayName, awayDisplayName, kickoffTime, dateLabel, shareChannels, strings)
                                shareGame("$homeDisplayName × $awayDisplayName", text)
                            }
                            style {
                                property("flex", "1 1 0%")
                                padding(14.px, 16.px)
                                backgroundColor(Color("#16181D"))
                                color(Color("#FFFFFF"))
                                borderRadius(14.px)
                                border(0.px)
                                fontFamily("Inter, sans-serif")
                                fontSize(15.px)
                                fontWeight(700)
                                letterSpacing((-0.2).px)
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                gap(8.px)
                                property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.12)")
                            }
                        }) {
                            LucideIcon(Icons.SHARE, size = 18, color = "#FFFFFF")
                            Text(strings.share)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GameDetailSkeleton() {
    Div({
        style {
            padding(16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(20.px)
        }
    }) {
        Div({
            style {
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(20.px)
                padding(24.px, 16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(16.px)
            }
        }) {
            SkeletonBox(width = 110.px, height = 22.px, borderRadius = 999.px)
            Div({
                style {
                    width(100.percent)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceAround)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    SkeletonBox(width = 52.px, height = 52.px, borderRadius = 26.px)
                    SkeletonBox(width = 90.px, height = 16.px, borderRadius = 4.px)
                }
                SkeletonBox(width = 48.px, height = 28.px, borderRadius = 8.px)
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    SkeletonBox(width = 52.px, height = 52.px, borderRadius = 26.px)
                    SkeletonBox(width = 90.px, height = 16.px, borderRadius = 4.px)
                }
            }
            SkeletonBox(width = 180.px, height = 12.px, borderRadius = 4.px)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
            }
        }) {
            SkeletonBox(width = 130.px, height = 14.px, borderRadius = 4.px)
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(2, 1fr)")
                    gap(10.px)
                }
            }) {
                repeat(2) {
                    Div({
                        style {
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(14.px)
                            padding(14.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                        }
                    }) {
                        SkeletonBox(width = 36.px, height = 36.px, borderRadius = 8.px)
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(6.px)
                            }
                        }) {
                            SkeletonBox(width = 76.px, height = 14.px, borderRadius = 4.px)
                            SkeletonBox(width = 52.px, height = 10.px, borderRadius = 4.px)
                        }
                    }
                }
            }
        }
    }
}
